
import { defineComponent } from 'vue'
import { AppButton } from '@shared/ui'
import { useOrderModel } from '@entities/order'
import { useMetaModel } from '@entities/meta'
import { initCallPrimeHill, useLoyaltyModel, verificationCodePrimeHill } from '@entities/loyalty'
import { Cookie } from '@shared/lib'

const VERIFIED_PHONE_COOKIE = 'primeHillCodeVerifiedPhone'

interface IData {
  countDown: number
  timerEnd: boolean
  digit1: string
  digit2: string
  digit3: string
  digit4: string
  interval: number | null
  isResending: boolean
  isSubmitting: boolean
  hasCodeError: boolean
  profileLoadError: boolean
}

export default defineComponent({
  components: { AppButton },
  emits: ['closeActiveModalBonus', 'changeActiveModalBonus'],
  setup () {
    const orderModel = useOrderModel()
    const loyaltyModel = useLoyaltyModel()
    const metaModel = useMetaModel()

    return {
      orderModel,
      loyaltyModel,
      metaModel
    }
  },
  data (): IData {
    return {
      countDown: 59,
      timerEnd: false,
      digit1: '',
      digit2: '',
      digit3: '',
      digit4: '',
      interval: null,
      isResending: false,
      isSubmitting: false,
      hasCodeError: false,
      profileLoadError: false
    }
  },

  computed: {
    code (): string {
      const code = this.digit1.concat(this.digit2, this.digit3, this.digit4)
      return code
    }
  },

  mounted () {
    this.interval = window.setInterval(this.countDownTimer, 1000)
  },

  // ПРИ РАЗМОНТИРОВАНИИ МОДАЛКИ ОЧИЩАЕМ setinterval
  beforeUnmount () {
    if (this.interval !== null) {
      clearInterval(this.interval)
    }
  },

  methods: {
    async closeModal () {
      if (this.code.length < 4 || this.isSubmitting) {
        return
      }

      const loyaltyCode = this.orderModel.orderData?.global_system_loyalty?.code
      if (loyaltyCode !== 'prime_hill') {
        this.$emit('changeActiveModalBonus', 'writeOffBonuses')
        return
      }

      const shopId = Number(this.orderModel.orderData?.settings?.shop_id)
      const phone = String(Cookie.get('primeHillPhone') ?? '').replace(/\D/g, '')
      if (!shopId || phone.length !== 11) {
        this.hasCodeError = true
        return
      }

      this.isSubmitting = true
      this.hasCodeError = false
      this.profileLoadError = false
      try {
        const isValidCode = await verificationCodePrimeHill(shopId, phone, this.code)
        if (!isValidCode) {
          this.hasCodeError = true
          this.resetCode()
          return
        }

        const sessionRaw = this.metaModel.session !== null ? parseFloat(String(this.metaModel.session)) : this.metaModel.session
        const session = sessionRaw ? Number(sessionRaw) : 0
        const userLoaded = await this.loyaltyModel.fetchUserPrimeHill({ tel: phone, session })
        if (!userLoaded) {
          this.profileLoadError = true
          return
        }

        Cookie.set(VERIFIED_PHONE_COOKIE, phone, 90)
        this.loyaltyModel.setVerifiedPhone(phone)
        this.$emit('changeActiveModalBonus', 'writeOffBonuses')
      } catch (e) {
        this.hasCodeError = true
        this.resetCode()
        // eslint-disable-next-line no-console
        console.log(e)
      } finally {
        this.isSubmitting = false
      }
    },

    countDownTimer () {
      if (this.countDown > 0) {
        this.countDown -= 1
      } else {
        if (this.interval !== null) {
          clearInterval(this.interval)
        }
        this.timerEnd = true
      }
    },

    async timerStart () {
      if (this.countDown !== 0 || this.isResending) {
        return
      }

      this.isResending = true
      const loyaltyCode = this.orderModel.orderData?.global_system_loyalty?.code
      const shopId = Number(this.orderModel.orderData?.settings?.shop_id)
      const phone = String(Cookie.get('primeHillPhone') ?? '').replace(/\D/g, '')

      try {
        if (loyaltyCode === 'prime_hill' && shopId && phone.length === 11) {
          await initCallPrimeHill(shopId, phone)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      } finally {
        this.isResending = false
      }

      this.countDown = 59
      this.timerEnd = false
      this.interval = window.setInterval(this.countDownTimer, 1000)
    },

    onDigitKeydown (event: KeyboardEvent, input: number) {
      if (event.key !== 'Backspace' || input <= 1) {
        return
      }
      const current = this[`digit${input}` as 'digit1' | 'digit2' | 'digit3' | 'digit4']
      if (current !== '') {
        return
      }
      event.preventDefault()
      if (this.hasCodeError) {
        this.hasCodeError = false
      }
      if (this.profileLoadError) {
        this.profileLoadError = false
      }
      const prevIndex = input - 1
      const key = `digit${prevIndex}` as 'digit1' | 'digit2' | 'digit3' | 'digit4'
      this[key] = ''
      this.$nextTick(() => {
        const prev = this.$refs['input' + prevIndex] as HTMLInputElement
        if (prev) {
          prev.value = ''
          prev.focus()
        }
      })
    },

    setDigit (event: Event, input: number) {
      const target = event.target as HTMLInputElement
      const inputEvent = event as InputEvent
      const onlyDigit = target.value.replace(/\D/g, '').slice(0, 1)
      if (this.hasCodeError) {
        this.hasCodeError = false
      }
      if (this.profileLoadError) {
        this.profileLoadError = false
      }
      target.value = onlyDigit
      this[`digit${input}` as 'digit1' | 'digit2' | 'digit3' | 'digit4'] = onlyDigit

      if (onlyDigit.length) {
        const next = this.$refs['input' + (input + 1)] as HTMLInputElement
        if (next) {
          next.focus()
        }
      } else if (inputEvent.inputType === 'deleteContentBackward' || inputEvent.inputType === 'deleteByCut') {
        const prev = this.$refs['input' + (input - 1)] as HTMLInputElement
        if (prev) {
          prev.focus()
        }
      }
    },

    resetCode () {
      this.digit1 = ''
      this.digit2 = ''
      this.digit3 = ''
      this.digit4 = ''

      this.$nextTick(() => {
        const first = this.$refs.input1 as HTMLInputElement
        if (first) {
          first.focus()
        }
      })
    }
  }
})
